import {
  latestWorkflowTestRunAttempts,
  RETRYABLE_TEST_RUN_STATUSES,
  type WorkflowTestStatus,
} from '@chatsdet/types';

export type TestRunRerunAvailability =
  | 'available'
  | 'suite_running'
  | 'test_running'
  | 'unavailable'
  /** A newer attempt exists for this run's (workflowId, scenarioId) bucket. */
  | 'superseded';

export type RerunButtonState = {
  isDisabled: boolean;
  tooltip: string;
};

type TestRunLike = {
  workflowId: string;
  status: WorkflowTestStatus;
  scenarioId?: string | null;
  attempt?: number | null;
  createdAt?: Date | string | null;
  rerunAvailability?: TestRunRerunAvailability;
};

const RETRYABLE_EXECUTION_STATUSES: ReadonlySet<string> = new Set([
  'queued',
  'running',
  'completed',
  'completed_with_errors',
  'completed_with_inconclusive',
]);

/**
 * Count latest retryable attempts that the API says can be rerun.
 *
 * Rerun availability is authoritative: the client does not infer active
 * layers or eligibility from the shape of the test-run list.
 */
export function getRetryableFailedCount(
  executionStatus: string | undefined,
  testRuns: readonly TestRunLike[]
): number {
  if (!executionStatus || !RETRYABLE_EXECUTION_STATUSES.has(executionStatus)) return 0;

  return latestWorkflowTestRunAttempts(testRuns).filter(
    (run) => RETRYABLE_TEST_RUN_STATUSES.has(run.status) && run.rerunAvailability === 'available'
  ).length;
}

export function getRerunButtonState(
  availability: TestRunRerunAvailability,
  isPending: boolean
): RerunButtonState {
  if (isPending) {
    return { isDisabled: true, tooltip: 'Starting rerun…' };
  }

  switch (availability) {
    case 'available':
      return { isDisabled: false, tooltip: 'Rerun this flow.' };
    case 'suite_running':
      return {
        isDisabled: true,
        tooltip: 'This flow can be rerun after the suite finishes.',
      };
    case 'test_running':
      return { isDisabled: true, tooltip: 'This flow is already running.' };
    case 'superseded':
      return {
        isDisabled: true,
        tooltip: 'A newer attempt exists for this flow — rerun that one instead.',
      };
    case 'unavailable':
      return { isDisabled: true, tooltip: 'This flow cannot be rerun.' };
  }
}
